// @ts-nocheck
import { useState, useEffect, useCallback } from 'react'
import { unlockAudioPlayback } from '../utils/audioUnlock'
import { Menu, PhoneOutgoing, PhoneIncoming } from 'lucide-react'
import { useCallStore } from '../stores/callStore'
import { useApi } from '../hooks/useApi'
import { OutboundCallDialog } from './OutboundCallDialog'
import RingAudioPromptModal from './RingAudioPromptModal'

/**
 * レイアウトのヘッダーコンポーネント
 * - サイドバートグル
 * - 組織名表示
 * - 受付状態トグルボタン
 * - 発信ボタン
 * - テスト着信ボタン
 */
export default function LayoutHeader({ sidebarOpen, setSidebarOpen, orgName }) {
  const toggleRingAudio = useCallStore((state) => state.toggleRingAudio)
  const ringAudioEnabled = useCallStore((state) => state.ringAudioEnabled)
  const callStatus = useCallStore((state) => state.callStatus)
  const isInCall = useCallStore((state) => state.isInCall)
  const [isOutboundDialogOpen, setIsOutboundDialogOpen] = useState(false)
  const [showPromptModal, setShowPromptModal] = useState(false)
  const [testCallLoading, setTestCallLoading] = useState(false)
  const api = useApi()

  // 通話中または接続中は発信ボタンを無効化
  const isCallActive = isInCall() || ['connecting', 'connected'].includes(callStatus)

  // ページロード時に着信音が無効な場合はモーダルを表示
  useEffect(() => {
    // 少し遅延させてからチェック（初期化を待つ）
    const timer = setTimeout(() => {
      if (!ringAudioEnabled) {
        setShowPromptModal(true)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [])

  // モーダルから受付開始
  const handleActivateFromModal = async () => {
    await toggleRingAudio()
    setShowPromptModal(false)
  }

  // テスト着信
  const handleTestCall = useCallback(async () => {
    setTestCallLoading(true)
    try {
      await api.post('/api/calls/test-call', {})
    } catch (err) {
      alert(`テスト着信エラー: ${err.message}`)
    } finally {
      setTestCallLoading(false)
    }
  }, [api])

  return (
    <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4">
        {/* 左側: メニューボタンとページタイトル */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {orgName}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {new Date().toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long'
              })}
            </p>
          </div>
        </div>

        {/* 右側 */}
        <div className="flex items-center gap-3">
          {/* 発信ボタン */}
          <button
            onClick={() => setIsOutboundDialogOpen(true)}
            disabled={isCallActive}
            className={`px-4 py-2 text-sm font-semibold rounded-lg border shadow-sm flex items-center gap-2 transition-colors ${
              isCallActive
                ? 'bg-gray-300 text-gray-500 border-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700'
            }`}
            title={isCallActive ? '通話中は発信できません' : '外線発信'}
          >
            <PhoneOutgoing className="w-4 h-4" />
            <span>発信</span>
          </button>

          <button
            onClick={async () => {
              // iOS Safari: タップの同期文脈で音声再生を解錠 (着信音・通話音声)
              unlockAudioPlayback()
              await toggleRingAudio()
            }}
            className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all ${
              ringAudioEnabled
                ? 'bg-green-600 text-white border border-green-700 hover:bg-green-700 shadow-sm'
                : 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-2 border-orange-600 hover:from-orange-600 hover:to-red-600 animate-pulse shadow-lg hover:shadow-xl font-bold'
            }`}
          >
            {ringAudioEnabled ? (
              <span>受付中</span>
            ) : (
              <>
                <PhoneIncoming className="w-4 h-4 animate-bounce" />
                <span>受付を開始 (着信音ON)</span>
              </>
            )}
          </button>

          {/* テスト着信ボタン */}
          <div className="ml-1 border-l border-gray-300 dark:border-gray-600 pl-3">
            <button
              onClick={handleTestCall}
              disabled={isCallActive || testCallLoading}
              className={`px-3 py-2 text-sm rounded-lg border flex items-center gap-1.5 transition-colors ${
                isCallActive || testCallLoading
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
              title="テスト着信を発生させます"
            >
              <PhoneIncoming className={`w-3.5 h-3.5 ${testCallLoading ? 'animate-pulse' : ''}`} />
              <span>テスト</span>
            </button>
          </div>
        </div>
      </div>

      {/* 発信ダイアログ */}
      <OutboundCallDialog
        isOpen={isOutboundDialogOpen}
        onClose={() => setIsOutboundDialogOpen(false)}
      />

      {/* 着信音有効化プロンプトモーダル */}
      <RingAudioPromptModal
        isOpen={showPromptModal}
        onActivate={handleActivateFromModal}
      />
    </header>
  )
}
